import React from 'react';

import { Currency } from 'components/common/DataFormat';
import Wahanda from 'common/wahanda';
import type { SharedProps } from 'reconnect-types/moduleFederation';
import { dataCell, rightAlignedHeader, stackedGroupHeader } from '../common/reportTableCells';

type ProductSalesMetrics = {
  numberIncludingCancelled?: number;
  grossTotalIncludingCancelled?: number;
  cancelledNumber?: number;
  cancelledGrossTotal?: number;
  number?: number;
  grossTotalLeftover?: number;
  paidBySalonVoucher?: number;
};

type ProductSalesRow = ProductSalesMetrics & {
  product: string;
};

export type ProductSalesReportTableProps = {
  rows: ProductSalesRow[];
  total: ProductSalesMetrics;
};

const buildDataCells = (row: ProductSalesMetrics) => [
  dataCell(row.numberIncludingCancelled ?? 0, { dataTestId: 'product-sale-quantity' }),
  dataCell(<Currency amount={row.grossTotalIncludingCancelled} />, {
    dataTestId: 'product-sale-amount',
  }),
  dataCell(row.cancelledNumber ?? 0, { dataTestId: 'product-refunds' }),
  dataCell(<Currency amount={row.cancelledGrossTotal} alwaysNegative />, {
    dataTestId: 'product-refunds-amount',
  }),
  dataCell(<Currency amount={row.paidBySalonVoucher ?? 0} />, { dataTestId: 'product-prepaid' }),
  dataCell(row.number ?? 0, { dataTestId: 'product-total-sale-quantity' }),
  dataCell(<Currency amount={row.grossTotalLeftover ?? 0} />, {
    variant: 'grandTotal',
    dataTestId: 'product-total-sale-amount',
  }),
];

export const getProductSalesReportTableProps = ({
  rows,
  total,
}: ProductSalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.productSalesReport.headings;

  const headerRows = [
    [
      { content: '\u00a0', width: 'wide' as const },
      stackedGroupHeader(headings.salesIncludingCancelled),
      stackedGroupHeader(headings.salesCancelled),
      {
        content: headings.prepaid,
        align: 'right' as const,
        width: 'narrow' as const,
        height: 'double' as const,
      },
      stackedGroupHeader(headings.salesTotal),
    ],
    [
      { content: headings.product, align: 'center' as const, width: 'narrow' as const },
      ...Array.from({ length: 2 }, () => [
        rightAlignedHeader(headings.quantity),
        rightAlignedHeader(headings.amountIncludingVat),
      ]).flat(),
      { content: '', align: 'right' as const, width: 'narrow' as const },
      rightAlignedHeader(headings.quantity),
      rightAlignedHeader(headings.amountIncludingVat),
    ],
  ];

  const tableRows = rows.map((row) => ({
    id: row.product,
    dataTestId: 'product-sale-row',
    cells: [{ content: row.product, dataTestId: 'product-name' }, ...buildDataCells(row)],
  }));

  const footerRow = {
    cells: [
      { content: headings.grandTotal },
      ...buildDataCells(total).map((cell) => ({ ...cell, variant: 'grandTotal' as const })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'product-sale-total',
    testID: 'product-sales-report-table',
  };
};
